import { BarChart3, BrainCircuit, Compass, LockKeyhole, type LucideIcon } from "lucide-react";

export type PlanKey = "flow" | "pulse" | "core" | "orbit";
export type PlanAvailability = "live" | "invite_only" | "coming_soon";

export type PricingPlan = {
  key: PlanKey;
  name: string;
  ecosystemName: string;
  monthlyPrice: number;
  availability: PlanAvailability;
  tagline: string;
  description: string;
  cta: string;
  icon: LucideIcon;
  highlighted?: boolean;
  discount?: {
    code: string;
    firstMonthPrice: number;
    label: string;
  };
  limits: string[];
  features: string[];
};

export const pricingPlans: PricingPlan[] = [
  {
    key: "flow",
    name: "Flow",
    ecosystemName: "SnapSolve Flow",
    monthlyPrice: 0,
    availability: "live",
    tagline: "Free",
    description: "A useful subscription tracker for the first people discovering Orizonte.",
    cta: "Start free",
    icon: BarChart3,
    limits: ["Track up to 5 subscriptions", "Manual add and CSV import", "Basic renewal calendar"],
    features: ["Dashboard overview", "Subscription list", "Basic spend analytics", "Renewal calendar"],
  },
  {
    key: "pulse",
    name: "Pulse",
    ecosystemName: "SnapSolve Pulse",
    monthlyPrice: 9,
    availability: "live",
    tagline: "Public beta",
    description: "The main paid beta plan for people who want Orizonte to actively keep the stack clean.",
    cta: "Get Pulse",
    icon: BrainCircuit,
    highlighted: true,
    discount: {
      code: "BETA3",
      firstMonthPrice: 3,
      label: "First month $3, then $9/mo",
    },
    limits: ["Unlimited tracked subscriptions", "Unlimited imports", "Priority renewal alerts"],
    features: ["AI overlap detection", "Ghost subscription flags", "Scenario simulator", "Advanced spend charts", "Meridian chat assistance"],
  },
  {
    key: "core",
    name: "Core",
    ecosystemName: "SnapSolve Core",
    monthlyPrice: 19,
    availability: "invite_only",
    tagline: "Invite only",
    description: "For early operators and power users helping shape the SnapSolve ecosystem before general release.",
    cta: "Request invite",
    icon: LockKeyhole,
    limits: ["Everything in Pulse", "Limited beta cohort", "Manual approval"],
    features: ["Future-stack buy decisions", "Deeper AI diagnostics", "Saved decision history", "Early ecosystem experiments", "Founder feedback lane"],
  },
  {
    key: "orbit",
    name: "Orbit",
    ecosystemName: "SnapSolve Orbit",
    monthlyPrice: 39,
    availability: "coming_soon",
    tagline: "Coming with Rallia + SideBy",
    description: "The cross-product command layer for Orizonte, Rallia, SideBy, and the wider SnapSolve suite.",
    cta: "Coming Soon",
    icon: Compass,
    limits: ["Everything in Core", "Launches after Rallia and SideBy", "Ecosystem-level memory"],
    features: ["Cross-app command center", "Multi-product automation", "Concierge workflows", "Shared SnapSolve intelligence graph", "Priority ecosystem rollout"],
  },
];

export const promoCodes = [
  {
    code: "BETA3",
    planKey: "pulse" as const,
    label: "First month $3, then $9/mo",
    firstMonthPrice: 3,
    monthlyPriceAfterPromo: 9,
    description: "Public beta founder discount for the first Orizonte users.",
  },
  {
    code: "SNAPSOLVE",
    planKey: "pulse" as const,
    label: "First month $3, then $9/mo",
    firstMonthPrice: 3,
    monthlyPriceAfterPromo: 9,
    description: "SnapSolve ecosystem beta discount.",
  },
];

export const getPricingPlan = (key?: string | null) => pricingPlans.find((plan) => plan.key === key) ?? pricingPlans[0];
